import { createServerStateStore } from "./serverState";

type Listener = () => void;

export type SmsTemplateKey =
  | "welcomeTempPassword"
  | "passwordReset"
  | "slaughterStarted"
  | "slaughterDone"
  | "deliveryAssigned"
  | "deliveryStarted"
  | "deliveryCompleted"
  | "paymentReceived";

export type SmsTemplateMap = Record<SmsTemplateKey, string>;
export type SmsTemplateEnabledMap = Record<SmsTemplateKey, boolean>;

export const SMS_TEMPLATE_LABELS: Record<SmsTemplateKey, string> = {
  welcomeTempPassword: "Yeni kullanıcı / geçici şifre",
  passwordReset: "Şifre sıfırlama",
  slaughterStarted: "Kesim başladı",
  slaughterDone: "Kesim tamamlandı",
  deliveryAssigned: "Teslimat atandı",
  deliveryStarted: "Taşıma başladı",
  deliveryCompleted: "Teslimat tamamlandı",
  paymentReceived: "Ödeme alındı",
};

export const TOGGLEABLE_SMS_TEMPLATES: SmsTemplateKey[] = [
  "slaughterStarted",
  "slaughterDone",
  "deliveryAssigned",
  "deliveryStarted",
  "deliveryCompleted",
  "paymentReceived",
];

export interface SmsTemplateState {
  templates: SmsTemplateMap;
  enabled: SmsTemplateEnabledMap;
}

export function isSmsTemplateToggleable(key: SmsTemplateKey) {
  return TOGGLEABLE_SMS_TEMPLATES.includes(key);
}

const defaultState: SmsTemplateState = {
  templates: {
    welcomeTempPassword:
      "{{customerName}}, Evren Çiftliği hesabınız oluşturuldu. Geçici şifreniz: {{password}}. İlk girişte şifrenizi değiştiriniz.",
    passwordReset:
      "{{customerName}}, Evren Çiftliği hesabınız için yeni geçici şifreniz: {{password}}. Giriş yaptıktan sonra şifrenizi değiştiriniz.",
    slaughterStarted:
      "{{customerName}}, {{kupeNo}} küpeli kurbanlığınız kesim sürecine alınmıştır.",
    slaughterDone:
      "{{customerName}}, {{kupeNo}} küpeli kurbanlığınızın kesim işlemi tamamlanmıştır.",
    deliveryAssigned:
      "{{customerName}}, teslimatınız hazırlandı. Taşıyıcı: {{carrierName}} ({{carrierPhone}}). Adres: {{address}}.",
    deliveryStarted:
      "{{customerName}}, teslimatınız yola çıktı. Taşıyıcı: {{carrierName}}. Canlı durum için taşıyıcınızla iletişime geçebilirsiniz.",
    deliveryCompleted:
      "{{customerName}}, teslimatınız tamamlandı. Afiyetle tüketmeniz dileğiyle.",
    paymentReceived:
      "{{customerName}}, ödemeniz alınmıştır. Mevcut bakiye: {{currentBalance}}. Alınan ödeme: {{paidAmount}}. Kalan bakiye: {{remainingBalance}}. Detayları müşteri portalı üzerinden de takip edebilirsiniz.",
  },
  enabled: {
    welcomeTempPassword: true,
    passwordReset: true,
    slaughterStarted: true,
    slaughterDone: true,
    deliveryAssigned: true,
    deliveryStarted: true,
    deliveryCompleted: true,
    paymentReceived: true,
  },
};

const store = createServerStateStore<SmsTemplateState>(
  "sms_templates",
  defaultState
);

export function getSmsTemplates(): SmsTemplateMap {
  return store.getState().templates;
}

export function getSmsTemplateEnabledMap(): SmsTemplateEnabledMap {
  return store.getState().enabled;
}

export function isSmsTemplateEnabled(key: SmsTemplateKey) {
  if (!isSmsTemplateToggleable(key)) return true;
  return store.getState().enabled[key] !== false;
}

export function subscribeSmsTemplates(listener: Listener) {
  return store.subscribe(listener);
}

export async function rehydrateSmsTemplates() {
  return store.rehydrate();
}

export async function updateSmsTemplate(key: SmsTemplateKey, value: string) {
  return store.setState((prev) => ({
    ...prev,
    templates: {
      ...prev.templates,
      [key]: value.trim(),
    },
  }));
}

export async function updateSmsTemplateEnabled(
  key: SmsTemplateKey,
  enabled: boolean
) {
  if (!isSmsTemplateToggleable(key)) return;

  return store.setState((prev) => ({
    ...prev,
    enabled: {
      ...prev.enabled,
      [key]: enabled,
    },
  }));
}

export async function resetSmsTemplates() {
  return store.reset();
}